import { generateLevel } from '../game/generation/generateLevel';
import { createLevelIdentity, type LevelIdentity } from '../game/model/identity';
import { computeBoardHash } from '../game/model/serialization';
import { validateSolution } from '../game/rules/moves';

/**
 * Browser-side test hooks.
 *
 * Compiled in only when `VITE_TEST_HOOKS=1` is set at build time (see `npm run build:e2e`),
 * so a production bundle contains none of this. It exists so Playwright can regenerate
 * levels inside Chromium, Firefox and WebKit and compare them against fixtures produced in
 * Node — the cross-runtime determinism guarantee.
 */

/** The parts of the renderer a browser test may inspect. */
export interface BoardSceneProbe {
  readonly renderedArrowCount: number;
}

export interface ArrowsTestApi {
  readonly version: 1;
  /** The board currently on screen, or undefined when no level is open. */
  boardScene?: BoardSceneProbe;
  boardHash(levelNumber: number, overrides?: Partial<Omit<LevelIdentity, 'levelNumber'>>): string;
  levelSummary(levelNumber: number): {
    boardHash: string;
    arrowCount: number;
    width: number;
    height: number;
    stencilId: string;
    difficultyBand: string;
    initialLegalMoves: number;
    solutionValid: boolean;
  };
}

declare global {
  interface Window {
    __arrowsTest?: ArrowsTestApi;
  }
}

export function testHooksEnabled(): boolean {
  return import.meta.env.VITE_TEST_HOOKS === '1';
}

/** Registers the live board so tests can compare what is drawn against the rules. */
export function registerTestBoardScene(scene: BoardSceneProbe | undefined): void {
  if (!testHooksEnabled()) return;
  const api = window.__arrowsTest;
  if (api === undefined) return;
  if (scene === undefined) delete api.boardScene;
  else api.boardScene = scene;
}

export function installTestHooks(): void {
  if (!testHooksEnabled()) return;

  const api: ArrowsTestApi = {
    version: 1,
    boardHash(levelNumber, overrides = {}) {
      return generateLevel(createLevelIdentity(levelNumber, overrides)).level.boardHash;
    },
    levelSummary(levelNumber) {
      const { level } = generateLevel(createLevelIdentity(levelNumber));
      return {
        boardHash: computeBoardHash(level.board),
        arrowCount: level.board.arrows.length,
        width: level.board.width,
        height: level.board.height,
        stencilId: level.stencilId,
        difficultyBand: level.recipe.difficultyBand,
        initialLegalMoves: level.analysis.initialLegalMoves,
        solutionValid: validateSolution(level.board, level.canonicalSolution).valid,
      };
    },
  };

  window.__arrowsTest = api;
}
